// mds-dojo — dijit/Tooltip — FAITHFUL port of references/dijit/Tooltip.js (OVERWRITES
// the earlier freestyle lite). The oracle drives dijit/Tooltip programmatically
// (new Tooltip({connectId, label})), so this is ported carefully. Contains the
// singleton _MasterTooltip (holds the actual markup, fades in/out via dojo/_base/fx +
// dijit/place.around) and the Tooltip widget (hover/focus state machine over connectIds).
// AMD → ESM + declare from the faithful runtime.
//
// Faithful adaptations (flagged):
//  1. dijit/_base/manager not ported; its only member used (defaultDuration) is inlined
//     from dojo/_base/config, matching dijit/_base/manager.js.
//  2. has("ie")/has("trident") branches dropped (evergreen): the orient() IE
//     offsetWidth "+2" word-wrap workaround and the _onShow() IE opacity-filter reset
//     become no-ops, exactly as dojo's non-IE path.
//  3. on.selector is not provided by mds dojo/on; a faithful-enough local onSelector()
//     (event delegation) backs Tooltip's `selector` option. Bubbling events (focusin/
//     focusout) delegate correctly; non-bubbling mouse enter/leave delegation is
//     best-effort. NOTE: the common path (no selector) is unaffected. Extension-event
//     dispatch (mouse.enter, template mouseenter/mouseleave) relies on the mds dojo/on
//     enhancement that calls function-type events as type(node, listener).
//  4. has("dojo-bidi") MasterTooltip.extend block dropped.
import array from '../dojo/_base/array';
import declare from '../dojo/_base/declare-runtime';
import fx from '../dojo/_base/fx';
import dom from '../dojo/dom';
import domClass from '../dojo/dom-class';
import domGeometry from '../dojo/dom-geometry';
import domStyle from '../dojo/dom-style';
import lang from '../dojo/_base/lang';
import mouse from '../dojo/mouse';
import on from '../dojo/on';
import config from '../dojo/_base/config';
import place from './place';
import _Widget from './_Widget';
import _TemplatedMixin from './_TemplatedMixin';
import BackgroundIframe from './BackgroundIframe';
import template from './templates/Tooltip.html?raw';
import dijit from './main';		// sets dijit.showTooltip etc. for back-compat

// module:
//		dijit/Tooltip

// dijit/_base/manager.defaultDuration, inlined (see header note 1).
var manager: any = { defaultDuration: (config as any).defaultDuration || 200 };

// Faithful-enough local port of dojo/on.selector (see header note 3). Returns an
// extension event (node, listener) => handle that delegates `eventType` to descendants
// of `node` matching `selector`, invoking listener with `this` = the matched node.
function onSelector(selector: any, eventType: any): any {
  return function (node: any, listener: any): any {
    var matchesTarget = function (evtTarget: any): any {
      var n = evtTarget;
      while (n && n !== node) {
        if (n.nodeType === 1 && (n.matches ? n.matches(selector) : false)) { return n; }
        n = n.parentNode;
      }
      return null;
    };
    var handler = function (evt: any): any {
      var matched = matchesTarget(evt.target);
      if (matched) {
        return listener.call(matched, evt);
      }
    };
    return on(node, eventType, handler);
  };
}

var MasterTooltip: any = declare('dijit._MasterTooltip', [_Widget, _TemplatedMixin], {
  // summary:
  //		Internal widget that holds the actual tooltip markup, which occurs once per page.

  // duration: Integer
  //		Milliseconds to fade in/fade out
  duration: manager.defaultDuration,

  templateString: template,

  postCreate: function (this: any) {
    this.ownerDocumentBody.appendChild(this.domNode);

    this.bgIframe = new (BackgroundIframe as any)(this.domNode);

    // Setup fade-in and fade-out functions.
    this.fadeIn = fx.fadeIn({ node: this.domNode, duration: this.duration, onEnd: lang.hitch(this, '_onShow') });
    this.fadeOut = fx.fadeOut({ node: this.domNode, duration: this.duration, onEnd: lang.hitch(this, '_onHide') });
  },

  show: function (this: any, innerHTML: any, aroundNode: any, position: any, rtl: any, textDir: any, onMouseEnter: any, onMouseLeave: any) {
    // summary:
    //		Display tooltip w/specified contents to right of specified node

    if (this.aroundNode && this.aroundNode === aroundNode && this.containerNode.innerHTML == innerHTML) {
      return;
    }

    if (this.fadeOut.status() == 'playing') {
      // previous tooltip is being hidden; wait until the hide completes then show new one
      this._onDeck = arguments;
      return;
    }
    this.containerNode.innerHTML = innerHTML;

    if (textDir) {
      this.set('textDir', textDir);
    }

    this.containerNode.align = rtl ? 'right' : 'left'; //fix the text alignment

    var pos = place.around(this.domNode, aroundNode,
      position && position.length ? position : Tooltip.defaultPosition, !rtl, lang.hitch(this, 'orient'));

    // Position the tooltip connector for middle alignment.
    var aroundNodeCoords = pos.aroundNodePos;
    if (pos.corner.charAt(0) == 'M' && pos.aroundCorner.charAt(0) == 'M') {
      this.connectorNode.style.top = aroundNodeCoords.y + ((aroundNodeCoords.h - this.connectorNode.offsetHeight) >> 1) - pos.y + 'px';
      this.connectorNode.style.left = '';
    } else if (pos.corner.charAt(1) == 'M' && pos.aroundCorner.charAt(1) == 'M') {
      this.connectorNode.style.left = aroundNodeCoords.x + ((aroundNodeCoords.w - this.connectorNode.offsetWidth) >> 1) - pos.x + 'px';
    } else {
      // Not *-centered, but just above/below/after/before
      this.connectorNode.style.left = '';
      this.connectorNode.style.top = '';
    }

    // show it
    domStyle.set(this.domNode, 'opacity', 0);
    this.fadeIn.play();
    this.isShowingNow = true;
    this.aroundNode = aroundNode;

    this.onMouseEnter = onMouseEnter || noop;
    this.onMouseLeave = onMouseLeave || noop;
  },

  orient: function (this: any, /*DomNode*/ node: any, /*String*/ aroundCorner: any, /*String*/ tooltipCorner: any, /*Object*/ spaceAvailable: any, /*Object*/ aroundNodeCoords: any) {
    // summary:
    //		Private function to set CSS for tooltip node based on which position it's in.

    this.connectorNode.style.top = ''; //reset to default

    var heightAvailable = spaceAvailable.h,
      widthAvailable = spaceAvailable.w;

    node.className = 'dijitTooltip ' +
      ({
        'MR-ML': 'dijitTooltipRight',
        'ML-MR': 'dijitTooltipLeft',
        'TM-BM': 'dijitTooltipAbove',
        'BM-TM': 'dijitTooltipBelow',
        'BL-TL': 'dijitTooltipBelow dijitTooltipABLeft',
        'TL-BL': 'dijitTooltipAbove dijitTooltipABLeft',
        'BR-TR': 'dijitTooltipBelow dijitTooltipABRight',
        'TR-BR': 'dijitTooltipAbove dijitTooltipABRight',
        'BR-BL': 'dijitTooltipRight',
        'BL-BR': 'dijitTooltipLeft'
      } as any)[aroundCorner + '-' + tooltipCorner];

    // reset width; it may have been set by orient() on a previous tooltip show()
    this.domNode.style.width = 'auto';

    // Reduce tooltip's width to the amount of width available, so that it doesn't overflow screen.
    var size = domGeometry.position(this.domNode);
    // has("ie")/has("trident") offsetWidth "+2" workaround dropped (evergreen).

    var width = Math.min((Math.max(widthAvailable, 1)), size.w);

    domGeometry.setMarginBox(this.domNode, { w: width });

    // Reposition the tooltip connector.
    if (tooltipCorner.charAt(0) == 'B' && aroundCorner.charAt(0) == 'B') {
      var bb = domGeometry.position(node);
      var tooltipConnectorHeight = this.connectorNode.offsetHeight;
      if (bb.h > heightAvailable) {
        // The tooltip starts at the top of the page and will extend past the aroundNode
        var aroundNodePlacement = heightAvailable - ((aroundNodeCoords.h + tooltipConnectorHeight) >> 1);
        this.connectorNode.style.top = aroundNodePlacement + 'px';
        this.connectorNode.style.bottom = '';
      } else {
        // Align center of connector with center of aroundNode
        this.connectorNode.style.bottom = Math.min(
          Math.max(aroundNodeCoords.h / 2 - tooltipConnectorHeight / 2, 0),
          bb.h - tooltipConnectorHeight) + 'px';
        this.connectorNode.style.top = '';
      }
    } else {
      // reset the tooltip back to the defaults
      this.connectorNode.style.top = '';
      this.connectorNode.style.bottom = '';
    }

    return Math.max(0, size.w - widthAvailable);
  },

  _onShow: function () {
    // summary:
    //		Called at end of fade-in operation
    // has("ie") opacity-filter reset dropped (evergreen no-op).
  },

  hide: function (this: any, aroundNode: any) {
    // summary:
    //		Hide the tooltip

    if (this._onDeck && this._onDeck[1] == aroundNode) {
      // this hide request is for a show() that hasn't even started yet; just cancel the pending show()
      this._onDeck = null;
    } else if (this.aroundNode === aroundNode) {
      // this hide request is for the currently displayed tooltip
      this.fadeIn.stop();
      this.isShowingNow = false;
      this.aroundNode = null;
      this.fadeOut.play();
    } else {
      // just ignore the call, it's for a tooltip that has already been erased
    }

    this.onMouseEnter = this.onMouseLeave = noop;
  },

  _onHide: function (this: any) {
    // summary:
    //		Called at end of fade-out operation

    this.domNode.style.cssText = '';	// to position offscreen again
    this.containerNode.innerHTML = '';
    if (this._onDeck) {
      // a show request has been queued up; do it now
      this.show.apply(this, this._onDeck);
      this._onDeck = null;
    }
  }
});

dijit.showTooltip = function (innerHTML: any, aroundNode: any, position: any, rtl: any, textDir: any, onMouseEnter: any, onMouseLeave: any) {
  // summary:
  //		Static method to display tooltip w/specified contents in specified position.

  // After/before don't work, but for back-compat convert them to the working after-centered, before-centered.
  if (position) {
    position = array.map(position, function (val: any) {
      return ({ after: 'after-centered', before: 'before-centered' } as any)[val] || val;
    });
  }

  if (!Tooltip._masterTT) { dijit._masterTT = Tooltip._masterTT = new MasterTooltip(); }
  return Tooltip._masterTT.show(innerHTML, aroundNode, position, rtl, textDir, onMouseEnter, onMouseLeave);
};

dijit.hideTooltip = function (aroundNode: any) {
  // summary:
  //		Static method to hide the tooltip displayed via showTooltip()
  return Tooltip._masterTT && Tooltip._masterTT.hide(aroundNode);
};

// Possible states for a tooltip, see Tooltip.state property for definitions
var DORMANT = 'DORMANT',
  SHOW_TIMER = 'SHOW TIMER',
  SHOWING = 'SHOWING',
  HIDE_TIMER = 'HIDE TIMER';

function noop() { /* placeholder mouse enter/leave callback */ }

var Tooltip: any = declare('dijit.Tooltip', _Widget, {
  // summary:
  //		Pops up a tooltip (a help message) when you hover over a node.

  // label: String
  //		HTML to display in the tooltip.
  label: '',

  // showDelay: Integer
  showDelay: 400,

  // hideDelay: Integer
  hideDelay: 400,

  // connectId: String|String[]|DomNode|DomNode[]
  connectId: [],

  // position: String[]
  position: [],

  // selector: String?
  selector: '',

  _setConnectIdAttr: function (this: any, /*String|String[]|DomNode|DomNode[]*/ newId: any) {
    // summary:
    //		Connect to specified node(s)

    // Remove connections to old nodes (if there are any)
    array.forEach(this._connections || [], function (nested: any) {
      array.forEach(nested, function (handle: any) { handle.remove(); });
    }, this);

    // Make array of id's to connect to, excluding entries for nodes that don't exist yet, see startup()
    this._connectIds = array.filter(lang.isArrayLike(newId) ? newId : (newId ? [newId] : []),
      function (this: any, id: any) { return dom.byId(id, this.ownerDocument); }, this);

    // Make connections
    this._connections = array.map(this._connectIds, function (this: any, id: any) {
      var node = dom.byId(id, this.ownerDocument),
        selector = this.selector,
        delegatedEvent = selector ?
          function (eventType: any) { return onSelector(selector, eventType); } :
          function (eventType: any) { return eventType; },
        self = this;
      return [
        on(node, delegatedEvent(mouse.enter), function (this: any) {
          self._onHover(this);
        }),
        on(node, delegatedEvent('focusin'), function (this: any) {
          self._onHover(this);
        }),
        on(node, delegatedEvent(mouse.leave), lang.hitch(self, '_onUnHover')),
        on(node, delegatedEvent('focusout'), lang.hitch(self, 'set', 'state', DORMANT))
      ];
    }, this);

    this._set('connectId', newId);
  },

  addTarget: function (this: any, /*DomNode|String*/ node: any) {
    // summary:
    //		Attach tooltip to specified node if it's not already connected

    var id = node.id || node;
    if (array.indexOf(this._connectIds, id) == -1) {
      this.set('connectId', this._connectIds.concat(id));
    }
  },

  removeTarget: function (this: any, /*DomNode|String*/ node: any) {
    // summary:
    //		Detach tooltip from specified node

    var id = node.id || node,	// map from DOMNode back to plain id string
      idx = array.indexOf(this._connectIds, id);
    if (idx >= 0) {
      // remove id (modifies original this._connectIds but that's OK in this case)
      this._connectIds.splice(idx, 1);
      this.set('connectId', this._connectIds);
    }
  },

  buildRendering: function (this: any) {
    this.inherited(arguments);
    domClass.add(this.domNode, 'dijitTooltipData');
  },

  startup: function (this: any) {
    this.inherited(arguments);

    // If this tooltip was created in a template, or for some other reason the specified connectId[s]
    // didn't exist during the widget's initialization, then connect now.
    var ids = this.connectId;
    array.forEach(lang.isArrayLike(ids) ? ids : [ids], this.addTarget, this);
  },

  getContent: function (this: any, /*DomNode*/ node: any) {
    // summary:
    //		User overridable function that return the text to display in the tooltip.
    return this.label || this.domNode.innerHTML;
  },

  // state: [private readonly] String
  state: DORMANT,
  _setStateAttr: function (this: any, val: any) {
    if (this.state == val ||
      (val == SHOW_TIMER && this.state == SHOWING) ||
      (val == HIDE_TIMER && this.state == DORMANT)) {
      return;
    }

    if (this._hideTimer) {
      this._hideTimer.remove();
      delete this._hideTimer;
    }
    if (this._showTimer) {
      this._showTimer.remove();
      delete this._showTimer;
    }

    switch (val) {
      case DORMANT:
        if (this._connectNode) {
          Tooltip.hide(this._connectNode);
          delete this._connectNode;
          this.onHide();
        }
        break;
      case SHOW_TIMER:	 // set timer to show tooltip
        // should only get here from a DORMANT state, i.e. tooltip can't be already SHOWING
        if (this.state != SHOWING) {
          this._showTimer = this.defer(function (this: any) { this.set('state', SHOWING); }, this.showDelay);
        }
        break;
      case SHOWING:		// show tooltip and clear timers
        var content = this.getContent(this._connectNode);
        if (!content) {
          this.set('state', DORMANT);
          return;
        }

        // Show tooltip and setup callbacks for mouseenter/mouseleave of tooltip itself
        Tooltip.show(content, this._connectNode, this.position, !this.isLeftToRight(), this.textDir,
          lang.hitch(this, 'set', 'state', SHOWING), lang.hitch(this, 'set', 'state', HIDE_TIMER));

        this.onShow(this._connectNode, this.position);
        break;
      case HIDE_TIMER:	// set timer set to hide tooltip
        this._hideTimer = this.defer(function (this: any) { this.set('state', DORMANT); }, this.hideDelay);
        break;
    }

    this._set('state', val);
  },

  _onHover: function (this: any, /*DomNode*/ target: any) {
    // summary:
    //		Despite the name of this method, it actually handles both hover and focus
    //		events on the target node, setting a timer to show the tooltip.

    if (this._connectNode && target != this._connectNode) {
      // Tooltip is displaying for another node
      this.set('state', DORMANT);
    }
    this._connectNode = target;		// _connectNode means "tooltip currently displayed for this node"

    this.set('state', SHOW_TIMER);	// no-op if show-timer already set, or if already showing
  },

  _onUnHover: function (this: any, /*DomNode*/ target: any) {
    // summary:
    //		Handles mouseleave event on the target node, hiding the tooltip.

    this.set('state', HIDE_TIMER);		// no-op if already dormant, or if hide-timer already set
  },

  // open() and close() aren't used anymore, except from the _BidiSupport/misc/Tooltip test.
  open: function (this: any, /*DomNode*/ target: any) {
    // summary:
    //		Display the tooltip; usually not called directly.

    this.set('state', DORMANT);
    this._connectNode = target;		// _connectNode means "tooltip currently displayed for this node"
    this.set('state', SHOWING);
  },

  close: function (this: any) {
    // summary:
    //		Hide the tooltip or cancel timer for show of tooltip

    this.set('state', DORMANT);
  },

  onShow: function (/*===== target, position =====*/) {
    // summary:
    //		Called when the tooltip is shown
  },

  onHide: function () {
    // summary:
    //		Called when the tooltip is hidden
  },

  destroy: function (this: any) {
    this.set('state', DORMANT);

    // Remove connections manually since they aren't registered to be removed by _WidgetBase
    array.forEach(this._connections || [], function (nested: any) {
      array.forEach(nested, function (handle: any) { handle.remove(); });
    }, this);

    this.inherited(arguments);
  }
});

Tooltip._MasterTooltip = MasterTooltip;		// for monkey patching
Tooltip.show = dijit.showTooltip;		// export function through module return value
Tooltip.hide = dijit.hideTooltip;		// export function through module return value

Tooltip.defaultPosition = ['after-centered', 'before-centered'];

export default Tooltip;
